import {useEffect, useState} from "react";
import {useCancellablePromise} from "helpers/promiseHandler";
import {Fetch} from "helpers/fetchWrapper";
import {getCookie} from "helpers/common";

const STATUS = {
    LOADING: "loading",
    SUCCESS: "success",
    ERROR: "error",
};

const authService = {
    _url: `${process.env.REACT_APP_RESTAURANT_MENU_URL}`,

    GetAllStores(businessId, fetch) {
        const [stores, setstores] = useState({
            stores: [],
            totalItems: 0,
        });
        const {cancellablePromise} = useCancellablePromise();
        const [storeStatus, setStoreStatus] = useState(STATUS.LOADING);
        useEffect(() => {
            setStoreStatus(STATUS.LOADING);
            cancellablePromise(this.getAllstores(businessId))
                .then(res => {
                    setstores(() => res);
                    setStoreStatus(STATUS.SUCCESS);
                })
                .catch(() => setStoreStatus(STATUS.ERROR));
        }, [businessId, fetch]);
        return {
            stores_loading: storeStatus === STATUS.LOADING,
            stores_error: storeStatus === STATUS.ERROR,
            stores_data: stores,
        };
    },

    GetBranchMenu(selectedStore, fetch) {
        const [stores, setstores] = useState({
            stores: [],
            totalItems: 0,
        });
        const {cancellablePromise} = useCancellablePromise();
        const [storeStatus, setStoreStatus] = useState(STATUS.LOADING);
        useEffect(() => {
            setStoreStatus(STATUS.LOADING);
            cancellablePromise(this.getBranchMenu(selectedStore))
                .then(res => {
                    setstores(() => res);
                    setStoreStatus(STATUS.SUCCESS);
                })
                .catch(() => setStoreStatus(STATUS.ERROR));
        }, [selectedStore, fetch]);
        return {
            stores_loading: storeStatus === STATUS.LOADING,
            stores_error: storeStatus === STATUS.ERROR,
            menu_data: stores,
        };
    },

    GetMenuById(menuId, fetch) {
        const [stores, setstores] = useState({
            stores: [],
            totalItems: 0,
        });
        const {cancellablePromise} = useCancellablePromise();
        const [storeStatus, setStoreStatus] = useState(STATUS.LOADING);
        useEffect(() => {
            setStoreStatus(STATUS.LOADING);
            cancellablePromise(this.getMenuById(menuId))
                .then(res => {
                    setstores(() => res);
                    setStoreStatus(STATUS.SUCCESS);
                })
                .catch(() => setStoreStatus(STATUS.ERROR));
        }, [menuId, fetch]);
        return {
            menu_loading: storeStatus === STATUS.LOADING,
            stores_error: storeStatus === STATUS.ERROR,
            menu: stores?.menu,
        };
    },

    GetAllCategories(menuId, fetch) {
        const [stores, setstores] = useState({
            stores: [],
            totalItems: 0,
        });
        const {cancellablePromise} = useCancellablePromise();
        const [storeStatus, setStoreStatus] = useState(STATUS.LOADING);
        useEffect(() => {
            console.log("GetAllCategories - useEffect triggered with menuId:", menuId);
            setStoreStatus(STATUS.LOADING);
            cancellablePromise(this.getAllcategories(menuId))
                .then(res => {
                    console.log("GetAllCategories - API response:", res);
                    setstores(() => res);
                    setStoreStatus(STATUS.SUCCESS);
                })
                .catch((error) => {
                    console.log("GetAllCategories - API error:", error);
                    setStoreStatus(STATUS.ERROR);
                });
        }, [menuId, fetch]);

        
        return {
            categories_loading: storeStatus === STATUS.LOADING,
            stores_error: storeStatus === STATUS.ERROR,
            categories_data: stores?.category,
        };
    },
    GetAllChoiceGroups(branchId, fetch) {
        const [choiceGroups, setchoiceGroups] = useState({
            choiceGroups: [],
            totalItems: 0,
        });
        const {cancellablePromise} = useCancellablePromise();
        const [choiceStatus, setchoiceStatus] = useState(STATUS.LOADING);
        useEffect(() => {
            setchoiceStatus(STATUS.LOADING);
            cancellablePromise(this.getChoiceGroups(branchId))
                .then(res => {
                    setchoiceGroups(() => res);
                    setchoiceStatus(STATUS.SUCCESS);
                })
                .catch(() => setchoiceStatus(STATUS.ERROR));
        }, [branchId, fetch]);
        return {
            choice_group_data_loading: choiceStatus === STATUS.LOADING,
            choice_error: choiceStatus === STATUS.ERROR,
            choice_group_data: choiceGroups,
        };
    },

    async getAllcategories(id = "") {
        if (!id || id === "undefined" || id === "null") {
            // Return proper structure with empty category array if no valid menu ID is provided
            return { category: [] };
        }
        let res = await Fetch.get(`${this._url}/category/getAll/${id}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async getAllstores(id = "") {
        let res = await Fetch.get(`${this._url}/stores/get-all-stores/${id}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async getBranchMenu(selectedStore = "") {
        let res = await Fetch.get(`${this._url}/menu/getBranchMenus/${selectedStore}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async getMenuById(menuId = "") {
        if (menuId !== "") {
            let res = await Fetch.get(`${this._url}/menu/getOneMenu/${menuId}`);
            if (res.status >= 200 && res.status < 300) {
                res = await res.json();
                return res;
            }
            const {message} = await res.json();
            throw new Error(message ?? "Something Went Wrong");
        }
    },

    async getMenuOverview(branchId = "") {
        if (branchId !== "") {
            let res = await Fetch.get(`${this._url}/menuItem/menu-overview/${branchId}`);
            if (res.status >= 200 && res.status < 300) {
                res = await res.json();
                return res;
            }
            const {message} = await res.json();
            throw new Error(message ?? "Something Went Wrong");
        }
    },

    async createMenu(payload) {
        let res = await Fetch.post(`${this._url}/menu/create`, payload);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async updateMenu(payload, menuId = "") {
        let res = await Fetch.put(`${this._url}/menu/update/${menuId}`, payload);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async createCategory(payload) {
        let res = await Fetch.post(`${this._url}/category/create`, payload);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async updateCategory(payload, categoryId = "") {
        let res = await Fetch.put(`${this._url}/category/updateOne/${categoryId}`, payload);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async deleteCategory(categoryId = "") {
        let res = await Fetch.delete(`${this._url}/category/deleteOne/${categoryId}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },



    async createItem(payload) {
        const headers = {
            "X-path": window.location.pathname,
            authorization: `Bearer ${getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE)}`,
        };

        try {
            const res = await fetch(`${this._url}/menuItem/create`, {
                method: "POST",
                headers,
                body: payload,
            });

            if (res.status >= 200 && res.status < 300) {
                return await res.json();
            }

            const {message} = await res.json();
            throw new Error(message ?? "Something Went Wrong");
        } catch (error) {
            console.error("Error creating menu item:", error);
            throw error;
        }
    },

    async deleteItem(itemId = "") {
        let res = await Fetch.delete(`${this._url}/menuItem/delete/${itemId}`);
    if (res.status >= 200 && res.status < 300) {
        res = await res.json();
        return res;
    }
    const {message} = await res.json();
    throw new Error(message ?? "Something Went Wrong");
    },

    async updateItem(payload, itemId = "") {
        const headers = {
            "X-path": window.location.pathname,
            authorization: `Bearer ${getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE)}`,
        };

        try {
            const res = await fetch(`${this._url}/menuItem/update/${itemId}`, {
                method: "PUT",
                headers,
                body: payload,
            });

            if (res.status >= 200 && res.status < 300) {
                return await res.json();
            }

            const {message} = await res.json();
            throw new Error(message ?? "Something Went Wrong");
        } catch (error) {
            console.error("Error creating menu item:", error);
            throw error;
        }
    },

    // Choice Group APIs
    async getChoiceGroups(branchId = "", search = "") {
        let url = `${this._url}/choiceGroup/branch/${branchId}`;
        if (search) {
            url += `?search=${search}`;
        }
        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async createChoiceGroup(payload) {
        let res = await Fetch.post(`${this._url}/choiceGroup`, payload);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async updateChoiceGroup(payload, choiceGroupId = "") {
        let res = await Fetch.put(`${this._url}/choiceGroup/${choiceGroupId}`, payload);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async deleteChoiceGroup(choiceGroupId = "") {
        let res = await Fetch.delete(`${this._url}/choiceGroup/${choiceGroupId}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async getChoiceGroupById(choiceGroupId = "") {
        let res = await Fetch.get(`${this._url}/choiceGroup/${choiceGroupId}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    // Combo APIs
    async getCombos(branchId = "", search = "") {
        let url = `${this._url}/combo/branch/${branchId}`;
        if (search) {
            url += `?search=${search}`;
        }
        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async createCombo(payload) {
        const headers = {
            "X-path": window.location.pathname,
            authorization: `Bearer ${getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE)}`,
        };

        try {
            const res = await fetch(`${this._url}/combo`, {
                method: "POST",
                headers,
                body: payload,
            });

            if (res.status >= 200 && res.status < 300) {
                return await res.json();
            }

            const {message} = await res.json();
            throw new Error(message ?? "Something Went Wrong");
        } catch (error) {
            console.error("Error creating combo:", error);
            throw error;
        }
    },

    async updateCombo(payload, comboId = "") {
        const headers = {
            "X-path": window.location.pathname,
            authorization: `Bearer ${getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE)}`,
        };

        try {
            const res = await fetch(`${this._url}/combo/${comboId}`, {
                method: "PUT",
                headers,
                body: payload,
            });

            if (res.status >= 200 && res.status < 300) {
                return await res.json();
            }

            const {message} = await res.json();
            throw new Error(message ?? "Something Went Wrong");
        } catch (error) {
            console.error("Error updating combo:", error);
            throw error;
        }
    },

    async deleteCombo(comboId = "") {
        let res = await Fetch.delete(`${this._url}/combo/${comboId}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    async getComboById(comboId = "") {
        let res = await Fetch.get(`${this._url}/combo/${comboId}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },

    //NEED TO REVIEW REDUNDANT API CALL CHECK FROM BACKEND

    async getMenuItemsByCategory(categoryId = "") {
        if (!categoryId || categoryId === "undefined" || categoryId === "null") {
            return { menuItems: [] };
        }
        let res = await Fetch.get(`${this._url}/menuItem/category/${categoryId}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something Went Wrong");
    },
};
export default authService;
